// Network Editor Constants

// Node Categories
export const NODE_CATEGORIES = {
  INPUT_OUTPUT: 'input_output',
  CORE: 'core',
  RECURRENT: 'recurrent',
  ATTENTION: 'attention',
  CONVOLUTIONAL: 'convolutional',
  UTILITY: 'utility',
  DATA_MANIPULATION: 'data_manipulation',
};

// Node Types
export const NODE_TYPES = {
  // Input & Output
  INPUT: 'Input',
  OUTPUT: 'Output',
  
  // Core Layers
  DENSE: 'Dense',
  ACTIVATION: 'Activation',
  DROPOUT: 'Dropout',
  BATCH_NORMALIZATION: 'BatchNormalization',
  LAYER_NORMALIZATION: 'LayerNormalization',
  
  // Recurrent Layers
  RNN: 'RNN',
  LSTM: 'LSTM',
  GRU: 'GRU',
  BIDIRECTIONAL: 'Bidirectional',
  TIME_DISTRIBUTED: 'TimeDistributed',
  REPEAT_VECTOR: 'RepeatVector',
  MASKING: 'Masking',
  
  // Attention & Transformer Layers
  SELF_ATTENTION: 'SelfAttention',
  MULTI_HEAD_ATTENTION: 'MultiHeadAttention',
  POSITIONAL_ENCODING: 'PositionalEncoding',
  TRANSFORMER_ENCODER: 'TransformerEncoder',
  TRANSFORMER_DECODER: 'TransformerDecoder',
  CAUSAL_MASK: 'CausalMask',
  
  // Convolutional Layers
  CONV_1D: 'Conv1D',
  DILATED_CONV_1D: 'DilatedConv1D',
  CONV_2D: 'Conv2D',
  MAX_POOLING_1D: 'MaxPooling1D',
  AVERAGE_POOLING_1D: 'AveragePooling1D',
  GLOBAL_MAX_POOLING_1D: 'GlobalMaxPooling1D',
  GLOBAL_AVERAGE_POOLING_1D: 'GlobalAveragePooling1D',
  
  // Tensor Manipulation
  FLATTEN: 'Flatten',
  RESHAPE: 'Reshape',
  PERMUTE: 'Permute',
  CONCATENATE: 'Concatenate',
  ADD: 'Add',
  MULTIPLY: 'Multiply',
  SPLIT: 'Split',
  SLICE: 'Slice',
  
  // Utilities
  LAMBDA: 'Lambda',
  EMBEDDING: 'Embedding',
  CUSTOM_LAYER: 'CustomLayer',
  CONDITION: 'Condition',
};

// Node categorization
export const NODE_CATEGORY_MAP = {
  [NODE_TYPES.INPUT]: NODE_CATEGORIES.INPUT_OUTPUT,
  [NODE_TYPES.OUTPUT]: NODE_CATEGORIES.INPUT_OUTPUT,
  
  [NODE_TYPES.DENSE]: NODE_CATEGORIES.CORE,
  [NODE_TYPES.ACTIVATION]: NODE_CATEGORIES.CORE,
  [NODE_TYPES.DROPOUT]: NODE_CATEGORIES.CORE,
  [NODE_TYPES.BATCH_NORMALIZATION]: NODE_CATEGORIES.CORE,
  [NODE_TYPES.LAYER_NORMALIZATION]: NODE_CATEGORIES.CORE,
  
  [NODE_TYPES.RNN]: NODE_CATEGORIES.RECURRENT,
  [NODE_TYPES.LSTM]: NODE_CATEGORIES.RECURRENT,
  [NODE_TYPES.GRU]: NODE_CATEGORIES.RECURRENT,
  [NODE_TYPES.BIDIRECTIONAL]: NODE_CATEGORIES.RECURRENT,
  [NODE_TYPES.TIME_DISTRIBUTED]: NODE_CATEGORIES.RECURRENT,
  [NODE_TYPES.REPEAT_VECTOR]: NODE_CATEGORIES.RECURRENT,
  [NODE_TYPES.MASKING]: NODE_CATEGORIES.RECURRENT,
  
  [NODE_TYPES.SELF_ATTENTION]: NODE_CATEGORIES.ATTENTION,
  [NODE_TYPES.MULTI_HEAD_ATTENTION]: NODE_CATEGORIES.ATTENTION,
  [NODE_TYPES.POSITIONAL_ENCODING]: NODE_CATEGORIES.ATTENTION,
  [NODE_TYPES.TRANSFORMER_ENCODER]: NODE_CATEGORIES.ATTENTION,
  [NODE_TYPES.TRANSFORMER_DECODER]: NODE_CATEGORIES.ATTENTION,
  [NODE_TYPES.CAUSAL_MASK]: NODE_CATEGORIES.ATTENTION,
  
  [NODE_TYPES.CONV_1D]: NODE_CATEGORIES.CONVOLUTIONAL,
  [NODE_TYPES.DILATED_CONV_1D]: NODE_CATEGORIES.CONVOLUTIONAL,
  [NODE_TYPES.CONV_2D]: NODE_CATEGORIES.CONVOLUTIONAL,
  [NODE_TYPES.MAX_POOLING_1D]: NODE_CATEGORIES.CONVOLUTIONAL,
  [NODE_TYPES.AVERAGE_POOLING_1D]: NODE_CATEGORIES.CONVOLUTIONAL,
  [NODE_TYPES.GLOBAL_MAX_POOLING_1D]: NODE_CATEGORIES.CONVOLUTIONAL,
  [NODE_TYPES.GLOBAL_AVERAGE_POOLING_1D]: NODE_CATEGORIES.CONVOLUTIONAL,
  
  // Data Manipulation category
  [NODE_TYPES.FLATTEN]: NODE_CATEGORIES.DATA_MANIPULATION,
  [NODE_TYPES.RESHAPE]: NODE_CATEGORIES.DATA_MANIPULATION,
  [NODE_TYPES.PERMUTE]: NODE_CATEGORIES.DATA_MANIPULATION,
  [NODE_TYPES.CONCATENATE]: NODE_CATEGORIES.DATA_MANIPULATION,
  [NODE_TYPES.ADD]: NODE_CATEGORIES.DATA_MANIPULATION,
  [NODE_TYPES.MULTIPLY]: NODE_CATEGORIES.DATA_MANIPULATION,
  [NODE_TYPES.SPLIT]: NODE_CATEGORIES.DATA_MANIPULATION,
  [NODE_TYPES.SLICE]: NODE_CATEGORIES.DATA_MANIPULATION,
  
  // Utility category
  [NODE_TYPES.LAMBDA]: NODE_CATEGORIES.UTILITY,
  [NODE_TYPES.EMBEDDING]: NODE_CATEGORIES.UTILITY,
  [NODE_TYPES.CUSTOM_LAYER]: NODE_CATEGORIES.UTILITY,
  [NODE_TYPES.CONDITION]: NODE_CATEGORIES.UTILITY,
};

// Available activation functions
export const ACTIVATION_FUNCTIONS = [
  'relu',
  'sigmoid',
  'tanh',
  'linear',
  'softmax',
  'selu',
  'elu',
  'gelu',
  'swish',
  'hard_sigmoid',
  'hard_swish',
  'softplus',
  'softsign',
  'exponential',
];

// Default node configurations
export const DEFAULT_NODE_CONFIGS = {
  [NODE_TYPES.INPUT]: {
    units: 10,
    shape: [10],
    batch_size: null,
  },
  [NODE_TYPES.OUTPUT]: {
    units: 1,
    activation: 'linear',
  },
  [NODE_TYPES.DENSE]: {
    units: 64,
    activation: 'relu',
    use_bias: true,
  },
  [NODE_TYPES.ACTIVATION]: {
    function: 'relu',
  },
  [NODE_TYPES.DROPOUT]: {
    rate: 0.2,
  },
  [NODE_TYPES.BATCH_NORMALIZATION]: {
    axis: -1,
    momentum: 0.99,
    epsilon: 0.001,
    center: true,
    scale: true,
  },
  [NODE_TYPES.LAYER_NORMALIZATION]: {
    axis: -1,
    epsilon: 0.001,
    center: true,
    scale: true,
  },
  [NODE_TYPES.RNN]: {
    units: 32,
    activation: 'tanh',
    return_sequences: true,
    return_state: false,
    go_backwards: false,
  },
  [NODE_TYPES.LSTM]: {
    units: 32,
    activation: 'tanh',
    recurrent_activation: 'sigmoid',
    use_bias: true,
    return_sequences: true,
    return_state: false,
    go_backwards: false,
  },
  [NODE_TYPES.GRU]: {
    units: 32,
    activation: 'tanh',
    recurrent_activation: 'sigmoid',
    use_bias: true,
    return_sequences: true,
    return_state: false,
    go_backwards: false,
  },
  [NODE_TYPES.BIDIRECTIONAL]: {
    layer_type: NODE_TYPES.LSTM,
    merge_mode: 'concat',
    return_sequences: true,
  },
  [NODE_TYPES.TIME_DISTRIBUTED]: {
    layer_type: NODE_TYPES.DENSE,
  },
  [NODE_TYPES.REPEAT_VECTOR]: {
    n: 3,
  },
  [NODE_TYPES.MASKING]: {
    mask_value: 0,
  },
  [NODE_TYPES.SELF_ATTENTION]: {
    num_heads: 8,
    key_dim: 64,
    dropout: 0,
  },
  [NODE_TYPES.MULTI_HEAD_ATTENTION]: {
    num_heads: 8,
    key_dim: 64,
    value_dim: null,
    dropout: 0,
  },
  [NODE_TYPES.POSITIONAL_ENCODING]: {
    max_length: 100,
    embedding_dim: 64,
  },
  [NODE_TYPES.TRANSFORMER_ENCODER]: {
    num_heads: 8,
    key_dim: 64,
    ff_dim: 256,
    dropout: 0.1,
  },
  [NODE_TYPES.TRANSFORMER_DECODER]: {
    num_heads: 8,
    key_dim: 64,
    ff_dim: 256,
    dropout: 0.1,
  },
  [NODE_TYPES.CAUSAL_MASK]: {
    mask_future: true,
    mask_diagonal: false,
  },
  [NODE_TYPES.CONV_1D]: {
    filters: 32,
    kernel_size: 3,
    strides: 1,
    padding: 'valid',
    dilation_rate: 1,
    activation: 'relu',
    use_bias: true,
  },
  [NODE_TYPES.DILATED_CONV_1D]: {
    filters: 32,
    kernel_size: 3,
    strides: 1,
    padding: 'valid',
    dilation_rate: 2,
    activation: 'relu',
    use_bias: true,
  },
  [NODE_TYPES.CONV_2D]: {
    filters: 32,
    kernel_size: [3, 3],
    strides: [1, 1],
    padding: 'valid',
    activation: 'relu',
    use_bias: true,
  },
  [NODE_TYPES.MAX_POOLING_1D]: {
    pool_size: 2,
    strides: null,
    padding: 'valid',
  },
  [NODE_TYPES.AVERAGE_POOLING_1D]: {
    pool_size: 2,
    strides: null,
    padding: 'valid',
  },
  [NODE_TYPES.GLOBAL_MAX_POOLING_1D]: {},
  [NODE_TYPES.GLOBAL_AVERAGE_POOLING_1D]: {},
  [NODE_TYPES.FLATTEN]: {},
  [NODE_TYPES.RESHAPE]: {
    target_shape: [-1, 1],
  },
  [NODE_TYPES.PERMUTE]: {
    dims: [2, 1],
  },
  [NODE_TYPES.CONCATENATE]: {
    axis: -1,
  },
  [NODE_TYPES.ADD]: {},
  [NODE_TYPES.MULTIPLY]: {},
  [NODE_TYPES.SPLIT]: {
    num_or_size_splits: 2,
    axis: -1,
  },
  [NODE_TYPES.SLICE]: {
    start: 0,
    size: null,
    axis: 0,
  },
  [NODE_TYPES.LAMBDA]: {
    expression: 'x * 2',
    output_shape: null,
  },
  [NODE_TYPES.EMBEDDING]: {
    input_dim: 1000,
    output_dim: 64,
    mask_zero: false,
  },
  [NODE_TYPES.CUSTOM_LAYER]: {
    code: 'return x',
  },
  [NODE_TYPES.CONDITION]: {
    condition: 'shape[0] > 0',
    true_branch: null,
    false_branch: null,
  },
};

// Fixed nodes IDs
export const INPUT_NODE_ID = 'fixed-input-node';
export const OUTPUT_NODE_ID = 'fixed-output-node'; 